import {
  CatalyticConverterSize,
  CatalyticConverterSizeOption,
  CatalyticItemGroup,
  CatalyticItemMetadata,
  GroupedCatalyticItems,
  InventoryItemRecord,
} from '../types/load';

export const isCatalyticConverter = (
  categoryName: string | undefined | null,
): boolean => categoryName?.toLowerCase().startsWith('catalytic') ?? false;

export const CATALYTIC_SIZE_OPTIONS: CatalyticConverterSizeOption[] = [
  { key: 'whole', label: 'Whole' },
  { key: 'threeQuarters', label: '3/4' },
  { key: 'half', label: '1/2' },
  { key: 'oneQuarter', label: '1/4' },
];

export const SIZE_PRIORITY: Record<CatalyticConverterSize, number> = {
  whole: 0,
  threeQuarters: 1,
  half: 2,
  oneQuarter: 3,
};

export const createGroupKey = (
  groupId: string | undefined,
  itemName: string,
): string => `${groupId ?? 'ungrouped'}_${itemName}`;

export const getCatalyticConverterSize = (
  metadata: unknown,
): CatalyticConverterSize | undefined => {
  const typedMetadata = metadata as CatalyticItemMetadata | undefined;

  return typedMetadata?.attributeValues
    ? JSON.parse(typedMetadata?.attributeValues)?.catalyticConverterSize
    : undefined;
};

const selectRepresentativeItem = (
  currentRep: InventoryItemRecord,
  candidate: InventoryItemRecord,
  candidateSize: CatalyticConverterSize,
): InventoryItemRecord => {
  if (candidateSize === 'whole') return candidate;

  const currentRepSize = getCatalyticConverterSize(currentRep.metadata);

  const shouldReplace =
    SIZE_PRIORITY[candidateSize] <
    SIZE_PRIORITY[currentRepSize ?? 'oneQuarter'];

  return shouldReplace ? candidate : currentRep;
};

const createInitialGroup = (
  item: InventoryItemRecord,
  groupKey: string,
): CatalyticItemGroup => ({
  groupKey,
  groupId: item.itemGroup?.id ?? 'ungrouped',
  groupName: item.itemGroup?.name ?? 'N/A',
  itemName: item.name,
  representativeItem: item,
  availableSizes: [],
  itemsBySize: new Map(),
});

const addItemToGroup = (
  group: CatalyticItemGroup,
  item: InventoryItemRecord,
  size: CatalyticConverterSize,
): CatalyticItemGroup => ({
  ...group,
  availableSizes: [...group.availableSizes, size],
  itemsBySize: new Map(group.itemsBySize).set(size, item),
  representativeItem: selectRepresentativeItem(
    group.representativeItem,
    item,
    size,
  ),
});

const sortGroupSizes = (group: CatalyticItemGroup): CatalyticItemGroup => ({
  ...group,
  availableSizes: [...group.availableSizes].sort(
    (a, b) => SIZE_PRIORITY[a] - SIZE_PRIORITY[b],
  ),
});

export const groupCatalyticItems = (
  items: InventoryItemRecord[],
): GroupedCatalyticItems => {
  const groupsByKey = items.reduce((acc, item) => {
    const groupKey = createGroupKey(item.itemGroup?.id, item.name);
    const size = getCatalyticConverterSize(item.metadata);
    const existingGroup = acc.get(groupKey);

    const baseGroup = existingGroup ?? createInitialGroup(item, groupKey);
    const updatedGroup = size
      ? addItemToGroup(baseGroup, item, size)
      : baseGroup;

    return new Map(acc).set(groupKey, updatedGroup);
  }, new Map<string, CatalyticItemGroup>());

  const sortedGroupsByKey = new Map(
    Array.from(groupsByKey.entries()).map(([key, group]) => [
      key,
      sortGroupSizes(group),
    ]),
  );

  const groups = Array.from(sortedGroupsByKey.values());
  const representativeItems = groups.map((g) => g.representativeItem);

  return {
    groups,
    groupsByKey: sortedGroupsByKey,
    representativeItems,
  };
};

export const getGroupForItem = (
  groupedData: GroupedCatalyticItems,
  item: InventoryItemRecord,
): CatalyticItemGroup | undefined => {
  const groupKey = createGroupKey(item.itemGroup?.id, item.name);
  return groupedData.groupsByKey.get(groupKey);
};

export const resolveWholeVariantId = (
  groupedData: GroupedCatalyticItems,
  item: InventoryItemRecord,
): string => {
  const group = getGroupForItem(groupedData, item);
  if (!group) return item.id;

  return (
    group.itemsBySize.get('whole')?.id ??
    group.itemsBySize.get(group.availableSizes[0])?.id ??
    item.id
  );
};

export const getRepresentativeItemId = (
  groupedData: GroupedCatalyticItems,
  item: InventoryItemRecord,
): string => {
  const group = getGroupForItem(groupedData, item);
  if (!group) return item.id;
  return group.representativeItem.id;
};
